import type {
  WellnessProgram,
  CreateWellnessProgramRequest,
  UpdateWellnessProgramRequest,
  AssignProgramRequest,
  ProgramAssignment,
  ProgramStatistics,
  WellnessFilters,
} from '../types/wellness';

// Simple fetch wrapper for wellness API
const API_BASE = '/api/wellness';

const fetchApi = async (path: string, options?: RequestInit) => {
  const token = localStorage.getItem('treel_token');
  const headers: HeadersInit = {
    'Content-Type': 'application/json',
    ...(token && { Authorization: `Bearer ${token}` }),
    ...(options?.headers || {}),
  };

  const response = await fetch(`${API_BASE}${path}`, {
    ...options,
    headers,
  });

  if (!response.ok) {
    const error = await response.json().catch(() => ({ error: 'Request failed' }));
    throw new Error(error.error || `Request failed with status ${response.status}`);
  }

  return response.json();
};

export class WellnessService {
  /**
   * Get all wellness programs with optional filters
   */
  static async getPrograms(filters?: WellnessFilters): Promise<WellnessProgram[]> {
    try {
      const params = new URLSearchParams();
      if (filters?.category) params.append('category', filters.category);
      if (filters?.isActive !== undefined) params.append('isActive', String(filters.isActive));
      
      const queryString = params.toString();
      const response = await fetchApi(`/programs${queryString ? `?${queryString}` : ''}`);
      return response.programs ?? [];
    } catch (error) {
      console.error('Failed to fetch wellness programs:', error);
      throw new Error('Failed to fetch wellness programs');
    }
  }

  /**
   * Get a specific wellness program by ID
   */
  static async getProgram(id: number): Promise<WellnessProgram> {
    try {
      const response = await fetchApi(`/programs/${id}`);
      if (!response.program) {
        throw new Error('Wellness program not found');
      }
      return response.program;
    } catch (error) {
      console.error(`Failed to fetch wellness program ${id}:`, error);
      throw new Error(`Failed to fetch wellness program: ${id}`);
    }
  }

  /**
   * Create a new wellness program (admin only)
   */
  static async createProgram(data: CreateWellnessProgramRequest): Promise<WellnessProgram> {
    try {
      const response = await fetchApi('/admin/programs', {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (!response.program) {
        throw new Error('Failed to create wellness program');
      }
      return response.program;
    } catch (error) {
      console.error('Failed to create wellness program:', error);
      throw new Error(error instanceof Error ? error.message : 'Failed to create wellness program');
    }
  }

  /**
   * Update an existing wellness program (admin only)
   */
  static async updateProgram(id: number, updates: UpdateWellnessProgramRequest): Promise<WellnessProgram> {
    try {
      const response = await fetchApi(`/admin/programs/${id}`, {
        method: 'PUT',
        body: JSON.stringify(updates),
      });
      if (!response.program) {
        throw new Error('Failed to update wellness program');
      }
      return response.program;
    } catch (error) {
      console.error(`Failed to update wellness program ${id}:`, error);
      throw new Error(`Failed to update wellness program: ${id}`);
    }
  }

  /**
   * Delete a wellness program (admin only)
   */
  static async deleteProgram(id: number): Promise<void> {
    try {
      await fetchApi(`/admin/programs/${id}`, {
        method: 'DELETE',
      });
    } catch (error) {
      console.error(`Failed to delete wellness program ${id}:`, error);
      throw new Error(`Failed to delete wellness program: ${id}`);
    }
  }

  /**
   * Assign a wellness program to a user (admin only)
   */
  static async assignProgram(programId: number, data: AssignProgramRequest): Promise<ProgramAssignment> {
    try {
      const response = await fetchApi(`/admin/programs/${programId}/assign`, {
        method: 'POST',
        body: JSON.stringify(data),
      });
      if (!response.assignment) {
        throw new Error('Failed to assign wellness program');
      }
      return response.assignment;
    } catch (error) {
      console.error(`Failed to assign wellness program ${programId}:`, error);
      throw new Error(`Failed to assign wellness program: ${programId}`);
    }
  }

  /**
   * Get user's wellness programs
   */
  static async getUserPrograms(userId: string): Promise<any> {
    try {
      const response = await fetchApi(`/users/${userId}/programs`);
      return response.programs ?? {};
    } catch (error) {
      console.error(`Failed to fetch user wellness programs for ${userId}:`, error);
      throw new Error(`Failed to fetch user wellness programs`);
    }
  }

  /**
   * Get program statistics (admin only)
   */
  static async getProgramStatistics(id: number): Promise<ProgramStatistics> {
    try {
      const response = await fetchApi(`/admin/programs/${id}/stats`);
      return response.stats ?? {
        programId: id,
        activeUsers: 0,
        completionRate: 0,
        avgDuration: 0
      };
    } catch (error) {
      console.error(`Failed to fetch program statistics for ${id}:`, error);
      throw new Error(`Failed to fetch program statistics`);
    }
  }
}